import { History } from "history";
import * as React from "react";
import { connect } from "react-redux";
import { Rnd } from "react-rnd";
import { match } from "react-router-dom";
import {
  addUserToReadByForBriefspread,
  AddUserToReadByForBriefspreadType,
  getComments,
  GetCommentsActionType
} from "src/commentView/actions";
import { isRequesting } from "src/requestProgress/helpers";
import { actions } from "src/requestProgress/reducer";
import { ErrorForceRefresh } from "src/shared/ErrorForceRefresh";
import {
  isCreditLensFeatureEnabled,
  isUnmappedAccountsFeatureEnabled
} from "../configState/selector";
import { getUser } from "../login/selector";
import { User } from "../login/types";
import LoadingSpinner from "../shared/LoadingSpinner";
import selectorsToProps from "../utils/selectorsToProps";
import ChartOfAccountsSet from "./chartOfAccounts/ChartOfAccountsSet";
import UnmappedAccounts from "./chartOfAccounts/unmappedAccounts/UnmappedAccounts";
import { deselectCells } from "./model/selectedCells/actions";
import { loadSpread, LoadSpreadActionType } from "./model/spread/actions";
import {
  getMappingNodeId,
  getSpreadAndCommentsAreLoaded,
  getSpreadError,
  getSpreadStatus
} from "./model/spread/selector";
import RawSpread from "./rawSpread/RawSpread";
import "./Spread.scss";

interface SpreadProps {
  loadSpread: LoadSpreadActionType;
  deselectCells: typeof deselectCells;
  spreadAndCommentsAreLoaded: boolean;
  match: match<{ id: string; environmentName?: string; status?: string }>;
  domRef: Document;
  user: User;
  isCreditLensFeatureEnabled: boolean;
  history: History;
  spreadErrorMessage?: string;
  mappingNodeId: string | null;
  unmappedAccountsEnabled: boolean;
  getComments: GetCommentsActionType;
  addUserToReadByForBriefspread: AddUserToReadByForBriefspreadType;
  hidden?: boolean;
  spreadStatus: string;
}

interface SpreadState {
  coaWidth: number;
  rawSpreadWidth: number;
  windowWidth: number;
  windowHeight: number;
  activeTab: number;
}

const resizeStyle = {
  display: "flex",
  justifyContent: "center"
};

const EnableCOA = {
  bottom: false,
  bottomLeft: false,
  bottomRight: false,
  left: false,
  right: true,
  top: false,
  topLeft: false,
  topRight: false
};

const EnableRawSpread = {
  bottom: false,
  bottomLeft: false,
  bottomRight: false,
  left: false,
  right: false,
  top: false,
  topLeft: false,
  topRight: false
};

const sideNavWidth = 70;
const topNavHeight = 50;
const coaWidthPercentage = 0.7;
const rawSpreadWidthPercentage = 0.3;
const minCoaWidthPercentage = 0.5;
const maxCoaWidthPercentage = 0.7;
const minRawSpreadPercentage = 0.3;
const maxRawSpreadPercentage = 0.5;

export class Spread extends React.Component<SpreadProps, SpreadState> {
  constructor(props: SpreadProps) {
    super(props);

    this.state = {
      windowWidth: 0,
      coaWidth: 0,
      rawSpreadWidth: 0,
      windowHeight: 0,
      activeTab: 0
    };
  }

  public handleResize = (
    e: any,
    direction: any,
    ref: any,
    delta: any,
    position: any
  ) => {
    this.onMouseUp();
    this.setState({
      coaWidth: ref.offsetWidth,
      rawSpreadWidth: this.state.windowWidth - ref.offsetWidth - sideNavWidth
    });
  };

  public handleWindowDimensions = () => {
    const topNav = this.props.domRef.getElementsByClassName("TopNav")[0];
    const topNavWidth = topNav.getBoundingClientRect().width;

    const sideNav =
      this.props.domRef.getElementsByClassName("SideNav")[0] ||
      this.props.domRef.getElementsByClassName("SideNav__open")[0];
    const windowHeight = sideNav.getBoundingClientRect().height + topNavHeight;

    this.setState({
      windowWidth: topNavWidth,
      windowHeight,
      coaWidth: topNavWidth * coaWidthPercentage - sideNavWidth,
      rawSpreadWidth: topNavWidth * rawSpreadWidthPercentage
    });
  };

  public componentDidMount() {
    const { id, environmentName, status } = this.props.match.params;
    const { isBankAnalyst, userId } = this.props.user;

    if (!this.props.spreadAndCommentsAreLoaded) {
      this.props.loadSpread({
        id,
        isBankAnalyst,
        userId,
        environmentName,
        status
      });
    }

    this.handleWindowDimensions();

    window.addEventListener("resize", this.handleWindowDimensions);
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.handleWindowDimensions);
  }

  public redirectToList = () => {
    this.props.history.push("/spreads");
  };

  public handleTabChange = (event: object, newValue: number) => {
    this.setState({ activeTab: newValue });
  };

  public render() {
    const { windowWidth, windowHeight } = this.state;
    const { spreadStatus, spreadErrorMessage } = this.props;

    if (spreadErrorMessage) {
      // Creditlens has requested we show a loading spinner if there is a 401 error.
      if (this.props.isCreditLensFeatureEnabled && spreadStatus !== "deleted") {
        return (
          <div className="Spread__container">
            <LoadingSpinner />
          </div>
        );
      }

      return (
        <ErrorForceRefresh
          errorMessage={this.props.spreadErrorMessage}
          status={spreadStatus}
        />
      );
    } else if (!this.props.spreadAndCommentsAreLoaded) {
      return (
        <div className="Spread__container">
          <LoadingSpinner />
        </div>
      );
    } else {
      return (
        <div onMouseDown={this.onMouseUp} className="Spread__container">
          <Rnd
            style={resizeStyle}
            size={{
              width: this.state.coaWidth,
              height: windowHeight - topNavHeight
            }}
            minWidth={windowWidth * minCoaWidthPercentage}
            maxWidth={windowWidth * maxCoaWidthPercentage - sideNavWidth}
            position={{ x: 0, y: 0 }}
            enableResizing={EnableCOA}
            disableDragging={true}
            onResize={this.handleResize}
          >
            <ChartOfAccountsSet
              environmentName={this.props.match.params.environmentName}
              redirectCallback={this.redirectToList}
              activeTab={this.state.activeTab}
              handleTabChange={this.handleTabChange}
            />
          </Rnd>
          <Rnd
            style={resizeStyle}
            size={{
              width: this.state.rawSpreadWidth,
              height: windowHeight - topNavHeight
            }}
            minWidth={windowWidth * minRawSpreadPercentage}
            maxWidth={windowWidth * maxRawSpreadPercentage}
            position={{ x: this.state.coaWidth, y: 0 }}
            enableResizing={EnableRawSpread}
            disableDragging={true}
          >
            <RawSpread
              environmentName={this.props.match.params.environmentName}
              activeTab={this.state.activeTab}
              hidden={this.props.hidden}
            />
          </Rnd>

          {this.props.unmappedAccountsEnabled ? <UnmappedAccounts /> : null}
        </div>
      );
    }
  }
  private onMouseUp = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    if (!isRequesting(actions.updateAccount) && !this.props.mappingNodeId) {
      this.props.deselectCells();
    }
  };
}

export default connect(
  selectorsToProps({
    isCreditLensFeatureEnabled,
    spreadAndCommentsAreLoaded: getSpreadAndCommentsAreLoaded,
    user: getUser,
    spreadErrorMessage: getSpreadError,
    mappingNodeId: getMappingNodeId,
    unmappedAccountsEnabled: isUnmappedAccountsFeatureEnabled,
    spreadStatus: getSpreadStatus
  }),
  {
    loadSpread: loadSpread.action,
    deselectCells,
    getComments: getComments.action,
    addUserToReadByForBriefspread: addUserToReadByForBriefspread.action
  }
)(Spread);



// WEBPACK FOOTER //
// ./src/spread/Spread.tsx